import React, { useState, useEffect, type FunctionComponent } from 'react'
import { authenticatedFetchData, API_HOST } from '../../../lib'
import { logError } from '@tomra/datadog-browser-logging'
import { PayoutsTable } from '../../common/PayoutsTable'
import { useParams } from 'react-router-dom'
import { Button } from '@tomra/design-system-react/button'

export const UserPayouts: FunctionComponent = () => {
  const { userId } = useParams<{ userId: string }>()
  const [userPayoutList, setUserPayoutList] = useState<PayoutType[]>([])
  const [requestStatus, setRequestStatus] = useState<string>('loading')
  const [limit, setLimit] = useState<number>(25)
  const canPotentiallyFetchMore = userPayoutList.length === limit

  const fetchMore = () => setLimit(limit + 25)

  useEffect(() => {
    limit > 25 ? setRequestStatus('loadingMore') : setRequestStatus('loading')

    const f = authenticatedFetchData(
      `${API_HOST}/mytomra/v1.0/hotlinetool/userpayout?userid=${encodeURIComponent(userId || '')}&limit=${limit}`
    )

    f.run().then(
      (result: PayoutType[]) => {
        setUserPayoutList(result)
        setRequestStatus('')
      },
      (error: Error) => {
        if (error.name !== 'AbortError') {
          setRequestStatus('failed')
          logError(new Error('Failed to fetch user payouts'), error)
        }
      }
    )

    return () => f.abort()
  }, [userId, limit])

  if (requestStatus === 'loading') {
    return (
      <div className="p-4">
        <div className="loadingSpinner" />
      </div>
    )
  }

  if (requestStatus === 'failed') {
    return (
      <div className="rounded-lg border-l-[3px] border-rose-500 bg-white p-4 text-left shadow-md dark:bg-gray-900 centerAbsolute">
        Whoopsie! Failed to fetch user payouts.
      </div>
    )
  }

  return userPayoutList.length > 0 ? (
    <>
      <PayoutsTable payouts={userPayoutList} />

      <div className="flex justify-center py-6">
        <Button variant="primary" disabled={!canPotentiallyFetchMore} onClick={fetchMore}>
          {requestStatus === 'loadingMore' ? <div className="loadingSpinner" /> : 'More'}
        </Button>
      </div>
    </>
  ) : (
    <div className="p-4">This user has no payouts yet</div>
  )
}
